import { useDebounce, useGetAddressV2 } from "@api";
import type { IAddress } from "@bbj-types/address";
import type { AutocompleteInputChangeReason } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDefaultValue } from "./format";

const TIMEOUT = 1000;

export interface IAddressOptions extends IAddress {
  isFavorite: boolean;
}

export const useAddressMeeting = (disableGoogle?: boolean) => {
  const { user } = useGlobalContext();
  const [{ loading: apiHookLoading, data: apiHookResult }, getApiHookResult] = useGetAddressV2(false);
  const [options, setOptions] = useState<IAddressOptions[]>([]);
  const [inputValue, setInputValue] = useState("");
  const favoriteOptions = useMemo(() => formatDefaultValue(user.favoriteAddresses) ?? [], [user.favoriteAddresses]);
  const accountUrl = `/v2/settings/user/${user._id}`;

  const filterFavorites = useCallback(
    (search: string) => {
      if (search === "" || search.length < 3) {
        return favoriteOptions;
      }
      const lower = search.toLowerCase();
      return favoriteOptions.filter(
        (fav) =>
          fav.formatted?.toLowerCase().includes(lower) ||
          fav.city?.toLowerCase().includes(lower) ||
          fav.complementaryAddress?.toLowerCase().includes(lower)
      );
    },
    [favoriteOptions]
  );

  const debounce = useDebounce(
    (newInputValue: string) => {
      setInputValue(newInputValue);
      if (newInputValue === "" || newInputValue.length < 3) {
        setOptions(favoriteOptions);
      } else {
        setOptions((prev) => [...prev.filter((o) => !o.isFavorite), ...filterFavorites(newInputValue)]);
        if (getApiHookResult) {
          void getApiHookResult(newInputValue, undefined, disableGoogle);
        }
      }
    },
    TIMEOUT,
    [getApiHookResult, favoriteOptions, filterFavorites]
  );

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, newInputValue: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "reset") {
        return;
      }
      debounce(newInputValue);
    },
    [debounce]
  );

  useEffect(() => {
    const searchAddresses: IAddressOptions[] = Array.isArray(apiHookResult)
      ? apiHookResult.map((address) => ({ ...address, isFavorite: false }))
      : [];
    setOptions([...searchAddresses, ...filterFavorites(inputValue)]);
  }, [apiHookResult, filterFavorites, inputValue]);

  return {
    options,
    favoriteOptions,
    apiHookLoading,
    handleInputChange,
    accountUrl,
    inputValue,
  };
};
